import { Link } from "react-router-dom";
import type { Role, Sale, SalePeriodTotals } from "../../api/types";
import { saleCostIsKnown } from "../../utils/salesTotals";
import {
  figureIfCostKnown,
  partlyKnownCost,
  salesWithUnknownCostNote,
} from "../../utils/unknownCost";
import { CostNote } from "../ui/CostNote";

interface Props {
  sales: Sale[];
  /** G-169: the totals of these sales, by the server's one rule (refunds already off). */
  totals: SalePeriodTotals | null;
  loading: boolean;
  error: string | null;
  role: Role;
}

const fmtEur = (n: number) => `€${n.toFixed(2)}`;

const fmtWhen = (iso: string) => {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

function lineSummary(sale: Sale): string {
  const labels = sale.lines.map((l) =>
    l.kind === "BOUQUET"
      ? l.bouquetBuild?.template?.name ?? "Custom bouquet"
      : `${Number(l.quantity)}× ${l.item?.name ?? "item"}`,
  );
  if (labels.length <= 2) return labels.join(", ");
  return `${labels.slice(0, 2).join(", ")} +${labels.length - 2}`;
}

export function PersonalSalesCard({ sales, totals, loading, error, role }: Props) {
  const showCogs = role === "OWNER" || role === "MANAGER";
  const limited = sales.slice(0, 10);

  // WO-B (OD-198): COGS and margin leave stock of unknown cost out — say how many sales.
  const unknownCostNote = salesWithUnknownCostNote(totals?.unknownCostSales);

  return (
    <section
      className="card"
      style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          flexWrap: "wrap",
          gap: "var(--space-3)",
        }}
      >
        <div>
          <h3 style={{ margin: 0 }}>My sales</h3>
          <p className="muted" style={{ margin: "var(--space-1) 0 0", fontSize: 13 }}>
            Tickets you have personally rung up.
          </p>
        </div>
        {showCogs ? (
          <Link className="button plain" to="/app/sales">
            Open shop&apos;s records →
          </Link>
        ) : null}
      </div>

      {error ? <div className="alert error">{error}</div> : null}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: showCogs
            ? "repeat(auto-fit, minmax(160px, 1fr))"
            : "repeat(auto-fit, minmax(180px, 1fr))",
          gap: "var(--space-3)",
        }}
      >
        <Stat label="Tickets" value={totals ? String(totals.tickets) : "—"} />
        <Stat label="Revenue" value={totals ? fmtEur(Number(totals.revenueEur)) : "—"} />
        {showCogs ? (
          <>
            <Stat label="Cost of goods" value={totals ? fmtEur(Number(totals.cogsEur)) : "—"} note={unknownCostNote} />
            {/* "Margin with IVA" (G-168): revenue − cost of goods, goods IVA still
                inside; the server adds it up (G-169) and returns no IVA figure for
                it yet (KI-47). */}
            <Stat label="Margin with IVA" value={totals ? fmtEur(Number(totals.grossProfitEur)) : "—"} note={unknownCostNote} />
          </>
        ) : null}
      </div>

      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>When</th>
              <th>Shop</th>
              <th>Items</th>
              <th style={{ textAlign: "right" }}>Revenue</th>
              {showCogs ? <th style={{ textAlign: "right" }}>Cost of goods</th> : null}
              {showCogs ? (
                <th style={{ textAlign: "right" }}>Margin with IVA</th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={showCogs ? 6 : 4} style={{ padding: "var(--space-4)" }}>
                  Loading…
                </td>
              </tr>
            ) : limited.length === 0 ? (
              <tr>
                <td colSpan={showCogs ? 6 : 4} className="empty-state">
                  No sales rung up yet.
                </td>
              </tr>
            ) : (
              limited.map((s) => (
                <tr key={s.id}>
                  <td>{fmtWhen(s.soldAt)}</td>
                  <td>
                    {s.location.name}{" "}
                    <span className="muted">({s.location.code})</span>
                  </td>
                  <td>{lineSummary(s)}</td>
                  <td style={{ textAlign: "right" }}>{fmtEur(Number(s.totalEur))}</td>
                  {showCogs ? (
                    <td style={{ textAlign: "right" }}>
                      {partlyKnownCost(s.totalCogsEur, !saleCostIsKnown(s), fmtEur)}
                    </td>
                  ) : null}
                  {showCogs ? (
                    <td style={{ textAlign: "right" }}>
                      {figureIfCostKnown(s.grossProfitEur, saleCostIsKnown(s), fmtEur)}
                    </td>
                  ) : null}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Stat({ label, value, note }: { label: string; value: string; note?: string | null }) {
  return (
    <div
      style={{
        padding: "var(--space-3)",
        borderRadius: "var(--radius-md)",
        background: "var(--color-surface-muted)",
      }}
    >
      <div
        className="muted"
        style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 0.5 }}
      >
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 600 }}>{value}</div>
      <CostNote note={note} />
    </div>
  );
}
